import { useState, useEffect } from "react"
import { Button } from "../ui/button"
import { cn } from "../../lib/utils"
import { useEncounter } from "../../contexts/EncounterContext"
import type { SpeakerLabel } from "../../contexts/EncounterContext"
import {
  AlertTriangle,
  Mic,
  Pause,
  Play,
  Square,
  Stethoscope,
  User,
  Users,
  Trash2,
} from "lucide-react"

const SPEAKERS: { label: SpeakerLabel; display: string; icon: React.ElementType; color: string }[] = [
  { label: "clinician", display: "Clinician", icon: Stethoscope, color: "bg-blue-500" },
  { label: "patient", display: "Patient", icon: User, color: "bg-green-500" },
  { label: "other", display: "Other", icon: Users, color: "bg-gray-400" },
]

const formatTime = (seconds: number): string => {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
}

function getDurationWarning(seconds: number): string | null {
  if (seconds >= 3300) return 'Approaching maximum recording duration'
  if (seconds >= 2700) return 'Recording approaching 45 minutes'
  if (seconds >= 1800) return 'Recording exceeding 30 minutes'
  return null
}

const EncounterRecorder = () => {
  const {
    status,
    error,
    isRecording,
    isPaused,
    recordingTime,
    currentSpeaker,
    failedChunkCount,
    exhaustedChunkCount,
    stopRecording,
    togglePause,
    markSpeaker,
    generateNote,
    resetEncounter,
  } = useEncounter()

  const [showDiscard, setShowDiscard] = useState(false)
  const [wakeLockFailed, setWakeLockFailed] = useState(false)

  // Keep screen awake during recording
  useEffect(() => {
    let wakeLock: WakeLockSentinel | null = null

    const requestWakeLock = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLock = await navigator.wakeLock.request('screen')
        }
      } catch (err) {
        console.warn('Wake lock not available:', err)
        setWakeLockFailed(true)
      }
    }

    if (isRecording) requestWakeLock()

    return () => {
      wakeLock?.release()
    }
  }, [isRecording])

  const currentSpeakerInfo = SPEAKERS.find((s) => s.label === currentSpeaker) ?? SPEAKERS[0]

  const cycleSpeaker = () => {
    const idx = SPEAKERS.findIndex((s) => s.label === currentSpeaker)
    const next = SPEAKERS[(idx + 1) % SPEAKERS.length]
    markSpeaker(next.label)
  }

  const handleStopAndGenerate = async () => {
    const ok = await stopRecording()
    if (ok) await generateNote()
  }

  const handleDiscard = () => {
    resetEncounter()
    setShowDiscard(false)
  }

  if (!isRecording && !isPaused && status !== 'error' && status !== 'processing' && status !== 'generating') return null

  const durationWarning = getDurationWarning(recordingTime)

  if (status === 'error') {
    return (
      <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-gray-900 text-white">
        <AlertTriangle className="h-12 w-12 text-red-400 mb-4" />
        <p className="text-xl font-medium mb-2">Something went wrong</p>
        <p className="text-sm text-gray-400 mb-8 max-w-md text-center px-4">
          {error || 'An unexpected error occurred during the encounter.'}
        </p>
        <div className="flex gap-3">
          <Button
            variant="outline"
            className="border-gray-600 bg-transparent text-white hover:bg-white/10"
            onClick={resetEncounter}
          >
            Dismiss
          </Button>
          <Button
            className="bg-blue-600 hover:bg-blue-700"
            onClick={resetEncounter}
          >
            Start New Recording
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-gray-900 text-white landscape:flex-row landscape:gap-12 sm:landscape:gap-16">
      {/* Left section in landscape: indicator + timer + speaker */}
      <div className="flex flex-col items-center landscape:flex-1 landscape:items-end">
        {/* Recording indicator */}
        <div className="flex items-center gap-3 mb-6 sm:mb-8">
          <div className={cn("relative h-4 w-4 rounded-full", isPaused ? "bg-yellow-400" : "bg-red-500")}>
            {!isPaused && (
              <span className="absolute inset-0 animate-ping rounded-full bg-red-500 opacity-75" />
            )}
          </div>
          <span className="text-sm font-medium uppercase tracking-wider text-gray-300">
            {isPaused ? "Paused" : "Recording"}
          </span>
        </div>

        {/* Timer */}
        <div className="text-5xl sm:text-7xl font-mono font-light tabular-nums mb-8 sm:mb-12">
          {formatTime(recordingTime)}
        </div>

        {/* Current speaker */}
        <button
          onClick={cycleSpeaker}
          className="flex items-center gap-2 rounded-full px-5 py-3 min-h-[48px] mb-8 sm:mb-10 transition-colors hover:bg-white/10 active:bg-white/20"
        >
          <div className={cn("h-3 w-3 rounded-full", currentSpeakerInfo.color)} />
          <currentSpeakerInfo.icon className="h-5 w-5" />
          <span className="text-lg">{currentSpeakerInfo.display}</span>
          <span className="text-xs text-gray-400 ml-1">tap to switch</span>
        </button>
      </div>

      {/* Right section in landscape: controls */}
      <div className="flex items-center gap-4 sm:gap-6 landscape:flex-1 landscape:items-start landscape:justify-start">
        <Button
          variant="outline"
          size="icon"
          className="h-12 w-12 sm:h-14 sm:w-14 rounded-full border-gray-600 bg-transparent text-white hover:bg-white/10 active:bg-white/20"
          onClick={togglePause}
        >
          {isPaused ? <Play className="h-6 w-6" /> : <Pause className="h-6 w-6" />}
        </Button>

        <Button
          className="h-14 sm:h-16 rounded-full bg-red-600 px-6 sm:px-8 text-base sm:text-lg font-medium hover:bg-red-700 active:bg-red-800"
          onClick={handleStopAndGenerate}
        >
          <Square className="h-5 w-5 mr-2" />
          Stop &amp; Generate
        </Button>

        <Button
          variant="outline"
          size="icon"
          className="h-12 w-12 sm:h-14 sm:w-14 rounded-full border-gray-600 bg-transparent text-white hover:bg-white/10 active:bg-white/20"
          onClick={() => setShowDiscard(true)}
        >
          <Trash2 className="h-5 w-5" />
        </Button>
      </div>

      {/* Duration warnings */}
      {durationWarning && (
        <div className={cn("text-sm px-4 py-2 rounded-lg mt-4 flex items-center gap-2",
          recordingTime >= 3300 ? "bg-red-500/20 text-red-300" : "bg-yellow-500/20 text-yellow-300"
        )}>
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {durationWarning}
        </div>
      )}

      {/* Failed chunk indicator */}
      {failedChunkCount > 0 && (
        <div className="text-xs px-3 py-1.5 rounded-lg mt-2 bg-orange-500/20 text-orange-300">
          {failedChunkCount} chunk{failedChunkCount > 1 ? 's' : ''} pending retry
        </div>
      )}

      {/* Exhausted chunk warning */}
      {exhaustedChunkCount > 0 && (
        <div className="text-xs px-3 py-1.5 rounded-lg mt-2 bg-red-500/20 text-red-300 flex items-center gap-2">
          <AlertTriangle className="h-3 w-3 shrink-0" />
          {exhaustedChunkCount} segment{exhaustedChunkCount > 1 ? 's' : ''} lost — note may be incomplete
        </div>
      )}

      {/* Wake lock failure warning */}
      {wakeLockFailed && isRecording && (
        <div className="text-xs px-3 py-1.5 rounded-lg mt-2 bg-yellow-500/20 text-yellow-300">
          Screen may turn off during recording. Keep the screen active manually.
        </div>
      )}

      {/* Mic icon watermark */}
      <Mic className="absolute bottom-8 h-8 w-8 text-gray-700" />

      {/* Discard confirmation */}
      {showDiscard && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/60">
          <div className="rounded-xl bg-gray-800 p-6 text-center max-w-sm">
            <p className="text-lg font-medium mb-2">Discard Recording?</p>
            <p className="text-sm text-gray-400 mb-6">
              This will permanently delete the current recording and transcript.
            </p>
            <div className="flex gap-3 justify-center">
              <Button variant="outline" className="border-gray-600 bg-transparent text-white hover:bg-white/10" onClick={() => setShowDiscard(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDiscard}>
                Discard
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default EncounterRecorder
